/**
 * Canonical folder + PII/vendor scrubbing for Spatial As-Built
 * archive extracts (zip/rar). Pure — no I/O.
 *
 * Display names land as `Drawings/A100.dwg` (folder + basename).
 * Storage object keys still use the basename only.
 */

import { fileExtension } from "@/lib/virtualspace/cad-bim-extensions";

export const ARCHIVE_CANONICAL_FOLDERS = [
  "Drawings",
  "Models",
  "PDFs",
  "Point Clouds",
  "Photos",
  "Other",
] as const;

export type ArchiveCanonicalFolder = (typeof ARCHIVE_CANONICAL_FOLDERS)[number];

export type ArchiveEntryNaming = {
  /** Display path recorded on `file_assets.file_name`. */
  displayName: string;
  /** Basename used for storage keys / extension checks. */
  baseName: string;
  folder: ArchiveCanonicalFolder;
};

const DRAWING_EXTS = new Set(["dwg", "dxf", "dgn", "pcp"]);
const MODEL_EXTS = new Set([
  "rvt",
  "rfa",
  "ifc",
  "skp",
  "nwd",
  "nwc",
  "3dm",
  "fbx",
  "obj",
  "step",
  "stp",
  "iges",
  "igs",
  "f3d",
  "sldprt",
  "sldasm",
  "prt",
  "asm",
  "catpart",
  "catproduct",
]);
const PDF_EXTS = new Set(["pdf"]);
const POINT_CLOUD_EXTS = new Set([
  "e57",
  "las",
  "laz",
  "rcp",
  "rcs",
  "pts",
  "ptx",
  "xyz",
  "ply",
]);
const PHOTO_EXTS = new Set([
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "heic",
  "heif",
  "tif",
  "tiff",
  "bmp",
  "dng",
  "svg",
]);

/** Folder synonyms → canonical (matched case-insensitively). */
const FOLDER_SYNONYMS: Record<string, ArchiveCanonicalFolder> = {
  drawing: "Drawings",
  drawings: "Drawings",
  cad: "Drawings",
  dwg: "Drawings",
  dxf: "Drawings",
  sheets: "Drawings",
  plans: "Drawings",
  "floor plans": "Drawings",
  model: "Models",
  models: "Models",
  bim: "Models",
  rvt: "Models",
  revit: "Models",
  ifc: "Models",
  "3d": "Models",
  pdf: "PDFs",
  pdfs: "PDFs",
  reports: "PDFs",
  documents: "PDFs",
  "point cloud": "Point Clouds",
  "point clouds": "Point Clouds",
  pointcloud: "Point Clouds",
  pointclouds: "Point Clouds",
  e57: "Point Clouds",
  matterpak: "Point Clouds",
  photo: "Photos",
  photos: "Photos",
  image: "Photos",
  images: "Photos",
  pics: "Photos",
  pictures: "Photos",
  other: "Other",
  misc: "Other",
};

const JUNK_FOLDER_NAMES = new Set([
  "__macosx",
  ".ds_store",
  "desktop",
  "downloads",
  "documents",
  "my documents",
  "mydocuments",
  "onedrive",
  "dropbox",
  "google drive",
  "googledrive",
  "icloud",
  "icloud drive",
  "temp",
  "tmp",
  "tmpfiles",
  "new folder",
  "untitled",
  "untitled folder",
  "archive",
  "archives",
  "export",
  "exports",
  "output",
  "outputs",
  "final",
  "finals",
  "delivery",
  "deliverables",
  "to send",
  "tosend",
  "for client",
  "forclient",
  "client delivery",
  "node_modules",
]);

const COMPANY_SUFFIX_RE =
  /\b(?:llc|l\.?l\.?c\.?|inc\.?|incorporated|ltd\.?|limited|corp\.?|corporation|llp|plc|gmbh|co\.?|company|studios?|architects?|designs?)\b/i;

/** "John Smith", "Mary-Jane O'Neil" — used on folder segments only. */
const PERSONAL_NAME_RE =
  /^[A-Z][a-z]+(?:[ '\u2019-][A-Z][a-z]+){1,2}$/;

/** Email crumbs glued onto stems (`Sheet_jane@x.com` → `Sheet`). */
const EMAIL_RE = /[_\-.\s]*[A-Za-z0-9.%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

/** Attribution crumbs often glued onto basenames. */
const ATTRIBUTION_RE =
  /\s*[(\[{]?\s*(?:by|from|prepared by|delivered by|modeled by|modelled by|drawn by|for)\s+[^)\]}/\\]+[)\]}]?\s*/gi;

export function archiveFolderForExtension(
  ext: string,
): ArchiveCanonicalFolder {
  const e = ext.toLowerCase();
  if (DRAWING_EXTS.has(e)) return "Drawings";
  if (MODEL_EXTS.has(e)) return "Models";
  if (PDF_EXTS.has(e)) return "PDFs";
  if (POINT_CLOUD_EXTS.has(e)) return "Point Clouds";
  if (PHOTO_EXTS.has(e)) return "Photos";
  return "Other";
}

function normalizeSegmentKey(segment: string): string {
  return segment
    .trim()
    .toLowerCase()
    .replace(/[_]+/g, " ")
    .replace(/\s+/g, " ");
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function stripKnownTokens(value: string, stripTokens: string[]): string {
  let out = value;
  for (const token of stripTokens) {
    const t = token.trim();
    if (t.length < 2) continue;
    out = out.replace(new RegExp(escapeRegExp(t), "gi"), " ");
  }
  return out.replace(/\s+/g, " ").trim();
}

/**
 * Drop or clean a path segment. Returns null when the segment
 * should not appear in the final display path.
 */
export function scrubArchivePathSegment(
  segment: string,
  stripTokens: string[] = [],
): string | null {
  let s = segment.trim();
  if (!s || s === "." || s === "..") return null;
  if (s.startsWith(".")) return null;

  s = s.replace(EMAIL_RE, " ").replace(/\s+/g, " ").trim();
  s = stripKnownTokens(s, stripTokens);
  if (!s) return null;

  const key = normalizeSegmentKey(s);
  if (JUNK_FOLDER_NAMES.has(key)) return null;
  if (FOLDER_SYNONYMS[key]) {
    // Synonym folders are represented by the canonical root —
    // don't keep a duplicate mid-path segment.
    return null;
  }

  // Vendor / company-looking folders
  if (COMPANY_SUFFIX_RE.test(s)) return null;

  // Personal-name folders (First Last) — never keep as path
  if (PERSONAL_NAME_RE.test(s)) return null;

  // "John's files", "files from Jane"
  if (/^(?:files?\s+)?(?:from|by)\s+/i.test(s)) return null;
  if (/\b(?:'s|’s)\s+(?:files?|folder|delivery|export|work)\b/i.test(s)) {
    return null;
  }

  // Collapse leftover punctuation noise
  s = s
    .replace(/[_\-–—]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!s) return null;
  if (s.length > 80) s = s.slice(0, 80).trim();
  return s;
}

export function scrubArchiveBaseName(
  baseName: string,
  stripTokens: string[] = [],
): string {
  const ext = fileExtension(baseName);
  const dot = baseName.lastIndexOf(".");
  let stem = dot > 0 ? baseName.slice(0, dot) : baseName;

  stem = stem.replace(EMAIL_RE, " ");
  stem = stem.replace(ATTRIBUTION_RE, " ");
  stem = stripKnownTokens(stem, stripTokens);

  // Trailing " - Vendor LLC" / " _ John Smith"
  stem = stem.replace(
    /\s*[-–—_]\s*[A-Z][A-Za-z0-9 .,'’-]{1,60}$/g,
    (tail) => {
      const body = tail.replace(/^[-–—_\s]+/, "");
      if (COMPANY_SUFFIX_RE.test(body) || PERSONAL_NAME_RE.test(body.trim())) {
        return "";
      }
      return tail;
    },
  );

  stem = stem
    .replace(/\s+/g, " ")
    .replace(/^[\s._\-–—]+|[\s._\-–—]+$/g, "")
    .trim();

  if (!stem) stem = "file";
  if (stem.length > 160) stem = stem.slice(0, 160).trim();

  return ext ? `${stem}.${ext}` : stem;
}

function canonicalFromSegments(
  segments: string[],
): ArchiveCanonicalFolder | null {
  for (const seg of segments) {
    const mapped = FOLDER_SYNONYMS[normalizeSegmentKey(seg)];
    if (mapped) return mapped;
  }
  return null;
}

/**
 * Keep at most one non-canonical subfolder when it looks like a
 * floor / discipline label (Level 1, Architectural, RCP, …).
 */
function keepSubfolder(segment: string): boolean {
  const key = normalizeSegmentKey(segment);
  if (
    /^(?:level|lvl|floor|fl|l)\s*-?\s*\d+[a-z]?$/.test(key) ||
    /^(?:basement|mezzanine|roof|penthouse|ground|podium)$/.test(key) ||
    /^(?:architectural|structural|mep|mechanical|electrical|plumbing|fire|civil)$/.test(
      key,
    ) ||
    /^(?:rcp|elevations?|sections?|details?|schedules?)$/.test(key)
  ) {
    return true;
  }
  return false;
}

export type NormalizeArchiveEntryOptions = {
  /** Known vendor label, org name, uploader display name, etc. */
  stripTokens?: string[];
};

/**
 * Turn an archive entry path into a canonical display name.
 *
 * Examples:
 *   `John Smith/CAD/A100.dwg` → `Drawings/A100.dwg`
 *   `Acme Modeling LLC/Pembroke.rvt` → `Models/Pembroke.rvt`
 *   `Drawings/Level 1/A101.dwg` → `Drawings/Level 1/A101.dwg`
 */
export function normalizeArchiveEntryPath(
  entryPath: string,
  opts: NormalizeArchiveEntryOptions = {},
): ArchiveEntryNaming | null {
  const stripTokens = (opts.stripTokens ?? [])
    .map((t) => t.trim())
    .filter((t) => t.length >= 2);

  const normalised = entryPath.replace(/\\/g, "/").replace(/^\/+/, "");
  if (!normalised || normalised.includes("..")) return null;

  const rawParts = normalised.split("/").filter(Boolean);
  if (rawParts.length === 0) return null;
  if (rawParts.some((p) => p === "__MACOSX" || p === ".DS_Store")) return null;

  const rawBase = rawParts[rawParts.length - 1];
  if (!rawBase || rawBase.startsWith(".") || rawBase === ".DS_Store") {
    return null;
  }

  const folderParts = rawParts.slice(0, -1);
  const synonymFolder = canonicalFromSegments(folderParts);
  const ext = fileExtension(rawBase);
  const folder =
    synonymFolder ?? archiveFolderForExtension(ext);

  const cleanedBase = scrubArchiveBaseName(rawBase, stripTokens);
  if (!cleanedBase || cleanedBase.startsWith(".")) return null;

  const keptSubs: string[] = [];
  for (const part of folderParts) {
    const cleaned = scrubArchivePathSegment(part, stripTokens);
    if (!cleaned) continue;
    if (FOLDER_SYNONYMS[normalizeSegmentKey(part)]) continue;
    if (keepSubfolder(cleaned) && keptSubs.length < 1) {
      keptSubs.push(cleaned);
    }
  }

  const displayParts = [folder, ...keptSubs, cleanedBase];
  const displayName = displayParts.join("/").slice(0, 240);

  return {
    displayName,
    baseName: cleanedBase,
    folder,
  };
}

/** Basename helper shared with sort/grouping when paths are present. */
export function archiveDisplayBaseName(
  fileName: string | null | undefined,
): string {
  if (!fileName) return "";
  const normalised = fileName.replace(/\\/g, "/");
  const parts = normalised.split("/").filter(Boolean);
  return parts[parts.length - 1] || fileName;
}
